<template>
  <AppModal
    :modelValue="modelValue"
    title="Compare versions"
    :subtitle="itemName"
    size="fullscreen"
    quiet
    hideCancel
    hideConfirm
    class="version-compare-modal"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <div v-if="modelValue" class="compare-layout">
    <div class="compare-wrap">
      <div class="compare-panes">
        <div
          v-for="side in SIDES"
          :key="side"
          class="compare-pane"
          :class="{ 'is-audible': audioSide === side }"
        >
          <div class="pane-head">
            <SearchableSelect
              :modelValue="selectedIds[side]"
              :items="versions"
              value-field="id"
              display-field="label"
              :hide-search="true"
              class="pane-version-select"
              @update:modelValue="selectVersion(side, $event)"
            >
              <template #item="{ item }">
                <span class="is-flex is-align-items-center" style="gap: 0.35rem">
                  {{ item.label }}
                  <span v-if="item.clientVersionNumber != null" class="tag is-link is-small">Client v{{ item.clientVersionNumber }}</span>
                  <span class="is-size-7 has-text-grey ml-auto">{{ item.date }}</span>
                </span>
              </template>
            </SearchableSelect>
            <span v-if="versionFor(side)?.clientVersionNumber != null" class="tag is-link">
              Client v{{ versionFor(side).clientVersionNumber }}
            </span>
            <button
              class="pane-audio-btn"
              :class="{ 'is-on': audioSide === side }"
              :title="audioSide === side ? 'Playing this version\'s audio' : 'Listen to this version'"
              @click="setAudioSide(side)"
            >
              <font-awesome-icon
                :icon="['fal', 'volume-xmark']"
                :active-icon="['fas', 'volume-high']"
                :active="audioSide === side"
              />
            </button>
          </div>
          <div class="pane-stage">
            <div v-if="errors[side]" class="pane-loading has-text-centered">
              <p class="is-size-7 mb-2">Couldn't load {{ versionFor(side)?.label || 'this version' }}</p>
              <button class="button is-small" @click="selectVersion(side, selectedIds[side], { fresh: true })">Retry</button>
            </div>
            <video
              v-else-if="urls[side]"
              :ref="(el) => (videoRefs[side] = el)"
              :src="needsHlsJs(urls[side]) ? undefined : urls[side]"
              :poster="posters[side] || undefined"
              :muted="audioSide !== side"
              playsinline
              preload="auto"
              @click="togglePlay"
              @loadedmetadata="onLoadedMetadata(side)"
              @timeupdate="onTimeUpdate(side)"
              @waiting="onWaiting(side)"
              @playing="onPlaying(side)"
              @ended="onEnded"
              @error="errors[side] = true"
            ></video>
            <div v-else class="pane-loading">
              <BrandLoader :size="'2x'" />
            </div>
          </div>
        </div>
      </div>

      <div class="compare-transport">
        <button class="transport-btn" :title="isPlaying ? 'Pause (Space)' : 'Play (Space)'" @mousedown.prevent @click="togglePlay">
          <font-awesome-icon
            :icon="['fas', 'play']"
            :active-icon="['fas', 'pause']"
            :active="isPlaying"
          />
        </button>
        <span class="transport-time">{{ formatTime(currentTime) }}</span>
        <input
          class="transport-scrub"
          type="range"
          min="0"
          :max="duration || 0"
          step="0.01"
          :value="currentTime"
          @input="seek(Number($event.target.value))"
        />
        <span class="transport-time">{{ formatTime(duration) }}</span>
        <div class="audio-toggle" title="Audio source (A)">
          <font-awesome-icon :icon="['fal', 'headphones']" class="mr-2 has-text-grey" />
          <button
            v-for="side in SIDES"
            :key="side"
            class="audio-toggle-btn"
            :class="{ 'is-on': audioSide === side }"
            @click="setAudioSide(side)"
          >{{ versionFor(side)?.label || '—' }}</button>
        </div>
      </div>
      <p class="compare-hint">Space play/pause · ←/→ step frame · A switch audio</p>
    </div>

    <!-- What was asked for on the older version: jump to each note and check the fix -->
    <aside class="compare-notes">
      <div class="notes-head">
        <span class="has-text-weight-semibold">Comments on {{ olderVersion?.label || 'older version' }}</span>
        <span class="is-size-7 has-text-grey">{{ openOlderCount }} open · click to jump</span>
      </div>
      <div v-if="olderComments.length === 0" class="notes-empty is-size-7 has-text-grey">
        No comments on this version.
      </div>
      <!-- Same markup/classes as ReviewCommentPanel (styles: _comment-panel.scss) -->
      <div v-else class="comment-list notes-list">
        <div
          v-for="thread in olderComments"
          :key="thread.id"
          class="comment-thread"
          :style="commentStyle(thread)"
        >
          <div class="comment-item" @click="jumpTo(thread.timestamp)">
            <div class="comment-header">
              <div class="avatar-circle" :style="{ background: thread.avatarColor }">
                <BunnyImage :fallback="thread.avatarImage" :alt="thread.author" img-class="avatar-img">
                  <template #placeholder>{{ thread.avatarInitials }}</template>
                </BunnyImage>
              </div>
              <div class="comment-meta">
                <div class="comment-name-row">
                  <span class="has-text-weight-semibold comment-author">{{ thread.author }}</span>
                  <button
                    v-if="thread.timestamp != null"
                    class="timestamp-badge"
                    title="Jump both players here"
                    @click.stop="jumpTo(thread.timestamp)"
                  >
                    <span class="icon is-small">
                      <font-awesome-icon :icon="['fal', 'clock']" />
                    </span>
                    {{ formatTimestamp(thread.timestamp) }}
                    <template v-if="thread.timestampEnd != null">
                      - {{ formatTimestamp(thread.timestampEnd) }}
                    </template>
                  </button>
                </div>
              </div>
              <div class="comment-actions">
                <span class="has-text-grey comment-time">{{ thread.createdAt }}</span>
                <button
                  class="resolve-btn"
                  :class="{ 'is-resolved': thread.resolved }"
                  :title="thread.resolved ? 'Mark unresolved' : 'Mark resolved'"
                  @click.stop="emit('toggle-resolved', thread)"
                >
                  <font-awesome-icon :icon="['fal', 'circle']" :active-icon="['fas', 'circle-check']" :active="thread.resolved" />
                </button>
              </div>
            </div>
            <p class="comment-text">{{ thread.text }}</p>
          </div>
        </div>
      </div>
    </aside>
    </div>
  </AppModal>
</template>

<script setup>
import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import { formatTimestamp, commentStyle } from '@/utils/reviewUtils'
import BunnyImage from '@/components/common/BunnyImage.vue'
import AppModal from '@/components/global/AppModal.vue'
import BrandLoader from '@/components/global/BrandLoader.vue'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import { logger } from '@/services/loggerService'
import { needsHlsJs, attachHlsJs } from '@/utils/hlsSource'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  itemName: { type: String, default: '' },
  // [{ id, label, clientVersionNumber, date }] — newest first, like activeItemVersions
  versions: { type: Array, default: () => [] },
  initialLeftId: { type: String, default: null },
  initialRightId: { type: String, default: null },
  // (versionId, { fresh }) => Promise<string> playback URL; fresh skips any cache
  resolveUrl: { type: Function, required: true },
  // (versionId) => Promise<string> thumbnail, shown until the video has a frame
  resolvePoster: { type: Function, default: null },
  // (versionId) => top-level comments on that version (reactive store read)
  commentsFor: { type: Function, default: () => [] },
})
const emit = defineEmits(['update:modelValue', 'toggle-resolved'])

const SIDES = ['left', 'right']
const DRIFT_TOLERANCE = 0.05
const SEEK_THRESHOLD = 0.5
const FRAME = 1 / 24 // ponytail: assumes 24fps, read version fps if frame-accurate stepping matters

const selectedIds = reactive({ left: null, right: null })
const urls = reactive({ left: '', right: '' })
const errors = reactive({ left: false, right: false })
const posters = reactive({ left: '', right: '' })
const videoRefs = reactive({ left: null, right: null })
// HLS playlists on non-native-HLS browsers attach through hls.js; each URL
// change mounts a fresh <video>, so attach whenever a pane's element appears.
const hlsDetach = { left: null, right: null }
for (const side of ['left', 'right']) {
  watch(() => videoRefs[side], async (el) => {
    hlsDetach[side]?.()
    hlsDetach[side] = null
    if (!el || !needsHlsJs(urls[side])) return
    const detach = await attachHlsJs(el, urls[side], () => { errors[side] = true })
    if (videoRefs[side] === el) hlsDetach[side] = detach
    else detach()
  })
}
const audioSide = ref('right')
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
// Sides currently stalled on network. While one buffers the other holds, so
// they stay in step without seeking (a seek is what causes buffering).
const buffering = reactive({ left: false, right: false })

const other = (side) => (side === 'left' ? 'right' : 'left')
const leader = () => videoRefs[audioSide.value]
const follower = () => videoRefs[other(audioSide.value)]
const versionFor = (side) => props.versions.find((v) => v.id === selectedIds[side])

// versions are newest-first, so the higher index is the older one
const olderVersion = computed(() => {
  const idx = (side) => props.versions.findIndex((v) => v.id === selectedIds[side])
  return idx('left') >= idx('right') ? versionFor('left') : versionFor('right')
})
const olderComments = computed(() => {
  if (!olderVersion.value) return []
  return [...props.commentsFor(olderVersion.value.id)]
    .sort((a, b) => (a.timestamp ?? Infinity) - (b.timestamp ?? Infinity))
})
const openOlderCount = computed(() => olderComments.value.filter((c) => !c.resolved).length)

function jumpTo(time) {
  if (time == null) return
  pauseBoth()
  seek(time)
}

async function selectVersion(side, versionId, { fresh = false } = {}) {
  if (!versionId) return
  selectedIds[side] = versionId
  urls[side] = ''
  errors[side] = false
  videoRefs[side] = null
  // Re-derive from the pane that stays loaded; the new one adds its length on load.
  duration.value = videoRefs[other(side)]?.duration || 0
  posters[side] = ''
  props.resolvePoster?.(versionId).then((poster) => {
    if (selectedIds[side] === versionId) posters[side] = poster || ''
  }).catch(() => {})
  const url = await props.resolveUrl(versionId, { fresh })
  // Drop a stale resolve if the user picked another version meanwhile.
  if (selectedIds[side] !== versionId) return
  urls[side] = url
  errors[side] = !url
}

watch(() => props.modelValue, (open) => {
  if (!open) {
    isPlaying.value = false
    currentTime.value = 0
    duration.value = 0
    urls.left = urls.right = ''
    return
  }
  audioSide.value = 'right'
  selectVersion('left', props.initialLeftId)
  selectVersion('right', props.initialRightId)
}, { immediate: true })

function eachVideo(fn) {
  for (const side of SIDES) if (videoRefs[side]) fn(videoRefs[side])
}

function playBoth() {
  const lead = leader()
  const follow = follower()
  if (!lead) return
  // Only seek the follower when it's genuinely off; seeking on every play
  // re-triggers buffering.
  if (follow && Math.abs(follow.currentTime - lead.currentTime) > DRIFT_TOLERANCE) {
    follow.currentTime = lead.currentTime
  }
  isPlaying.value = true
  eachVideo((v) => v.play().catch((err) => {
    logger.warn('[VersionCompareModal] play() rejected', { error: err?.message })
  }))
}

function pauseBoth() {
  isPlaying.value = false
  buffering.left = buffering.right = false
  eachVideo((v) => {
    v.pause()
    v.playbackRate = 1
  })
}

function togglePlay() {
  if (isPlaying.value) pauseBoth()
  else playBoth()
}

function seek(time) {
  const t = Math.max(0, Math.min(time, duration.value || time))
  eachVideo((v) => { v.currentTime = t })
  currentTime.value = t
  // Scrubbing back into a version that had already ended: pick it back up
  if (isPlaying.value) eachVideo((v) => { if (v.paused && t < v.duration) v.play().catch(() => {}) })
}

function setAudioSide(side) {
  audioSide.value = side
}

function onLoadedMetadata(side) {
  const v = videoRefs[side]
  if (!v) return
  // Versions can differ in length — scrub across the longer one.
  duration.value = Math.max(duration.value, v.duration || 0)
  // A pane that reloaded (version swap) rejoins at the shared position.
  if (currentTime.value) v.currentTime = currentTime.value
  if (isPlaying.value) v.play().catch(() => {})
}

// The clock follows the audible side, or whichever is still running once the
// shorter version has ended.
function clockSide() {
  return videoRefs[audioSide.value]?.ended ? other(audioSide.value) : audioSide.value
}

function onEnded() {
  // Let the longer version finish; stop only when both are done.
  if (SIDES.every((s) => !videoRefs[s] || videoRefs[s].ended)) pauseBoth()
}

function onTimeUpdate(side) {
  if (side !== clockSide()) return
  const lead = videoRefs[side]
  const follow = videoRefs[other(side)]
  if (!lead) return
  currentTime.value = lead.currentTime
  if (!follow || follow.ended || follow.seeking || !isPlaying.value) return
  const drift = lead.currentTime - follow.currentTime
  if (Math.abs(drift) > SEEK_THRESHOLD) {
    follow.currentTime = lead.currentTime
    follow.playbackRate = 1
  } else if (Math.abs(drift) > DRIFT_TOLERANCE) {
    // Small drift: nudge speed instead of seeking (no rebuffer)
    follow.playbackRate = drift > 0 ? 1.05 : 0.95
  } else {
    follow.playbackRate = 1
  }
}

function onWaiting(side) {
  if (!isPlaying.value) return
  buffering[side] = true
  videoRefs[other(side)]?.pause()
}

function onPlaying(side) {
  buffering[side] = false
  if (!isPlaying.value || buffering[other(side)]) return
  const peer = videoRefs[other(side)]
  if (peer?.paused && !peer.ended) peer.play().catch(() => {})
}

function onKeydown(e) {
  if (!props.modelValue) return
  if (e.target.closest?.('input, textarea, [contenteditable]') && e.target.type !== 'range') return
  if (e.key === ' ') {
    e.preventDefault()
    togglePlay()
  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
    e.preventDefault()
    pauseBoth()
    seek(currentTime.value + (e.key === 'ArrowRight' ? FRAME : -FRAME))
  } else if (e.key === 'a' || e.key === 'A') {
    setAudioSide(other(audioSide.value))
  }
}

watch(() => props.modelValue, (open) => {
  if (open) window.addEventListener('keydown', onKeydown)
  else window.removeEventListener('keydown', onKeydown)
}, { immediate: true })
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  hlsDetach.left?.()
  hlsDetach.right?.()
})

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}
</script>

<style lang="scss" scoped>
.version-compare-modal {
  :deep(.modal-card) {
    height: 100vh;
    max-height: 100vh;
  }

  :deep(.modal-card-body) {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}

.compare-layout {
  display: flex;
  gap: 1rem;
  flex: 1;
  min-height: 0;
}

.compare-wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  gap: 0.75rem;
}

.compare-notes {
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid var(--bulma-border, #dbdbdb);
  padding-left: 1rem;
}

.notes-head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--bulma-border-weak, #ededed);
}

.notes-empty {
  padding: 1rem 0;
}

.notes-list {
  padding: 0.5rem 0 0;
}

.compare-panes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
  flex: 1;
  min-height: 0;
  background: #0b0b0d;
  border-radius: 6px;
  overflow: hidden;
}

.compare-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  background: #0b0b0d;
  box-shadow: inset 0 -2px 0 transparent;
  transition: box-shadow 0.2s ease;

  &.is-audible {
    box-shadow: inset 0 -2px 0 var(--bulma-primary, #485fc7);
  }
}

.pane-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.04);
}

.pane-version-select {
  width: 160px;
}

.pane-audio-btn {
  margin-left: auto;
  border: none;
  background: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  font-size: 1rem;
  padding: 0.25rem;
  transition: color 0.15s ease;

  &:hover,
  &.is-on {
    color: #fff;
  }
}

.pane-stage {
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  // Absolute so the video's intrinsic size can't stretch the grid.
  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: pointer;
  }
}

.pane-loading {
  color: rgba(255, 255, 255, 0.6);
}

.compare-transport {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.transport-btn {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1.1rem;
  width: 2rem;
  color: var(--bulma-text-strong, #363636);
}

.transport-time {
  font-variant-numeric: tabular-nums;
  font-size: 0.8rem;
  color: var(--bulma-grey, #7a7a7a);
  min-width: 2.75rem;
}

.transport-scrub {
  flex: 1;
  accent-color: var(--bulma-primary, #485fc7);
}

.audio-toggle {
  display: flex;
  align-items: center;
}

.audio-toggle-btn {
  border: 1px solid var(--bulma-border, #dbdbdb);
  background: none;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0.2rem 0.6rem;
  color: var(--bulma-grey, #7a7a7a);

  &:first-of-type {
    border-radius: 4px 0 0 4px;
  }

  &:last-of-type {
    border-radius: 0 4px 4px 0;
    border-left: none;
  }

  &.is-on {
    border-color: var(--bulma-primary, #485fc7);
    color: var(--bulma-primary, #485fc7);
    font-weight: 600;
  }
}

.compare-hint {
  font-size: 0.7rem;
  color: var(--bulma-grey-light, #b5b5b5);
  text-align: right;
}
</style>
